import React, { useMemo } from 'react';
import type {
  ColDef,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import { getAgendaActions, saveAgendaActionsBatch } from '../../api/agenda';
import { useAgendaCategories } from '../../hooks/useAgendaCategories';
import { AGENDA_SCOPES, type AgendaAction } from '../../types/Agenda';
import { t, type MessageKey } from '../../i18n/t';

const newDefaults = (): Partial<AgendaAction> => ({
  recId: 0,
  scope: 'tenant',
  actionCode: null,
  categoryId: 0,
  actionName: '',
  actionDescription: null,
  dueSunday: false,
  dueMonday: false,
  dueTuesday: false,
  dueWednesday: false,
  dueThursday: false,
  dueFriday: false,
  beforeOpenDay: false,
  required4ValuationIsraeli: false,
  required4ValuationForeign: false,
  logicalOrder: null,
  ask4Comment: false,
  rowColor: null,
  isActive: true,
});

// Boolean day/flag column factory — keeps the wide grid declarative.
function boolCol(field: keyof AgendaAction, width = 110): ColDef<AgendaAction> {
  return { field, headerName: t(`agendaActions.columns.${field}` as MessageKey), width, editable: true, cellDataType: 'boolean' };
}

export function AgendaActionsPage() {
  const { data: categories, loading, error } = useAgendaCategories();

  const columnDefs = useMemo<ColDef<AgendaAction>[]>(() => {
    if (!categories) return [];

    const labelById = new Map<number, string>(
      categories.map((c) => [c.categoryId, `${c.categoryId} - ${c.categoryName}`]),
    );
    const idByLabel = new Map<string, number>(
      categories.map((c) => [`${c.categoryId} - ${c.categoryName}`, c.categoryId]),
    );
    const categoryValues = categories.map((c) => `${c.categoryId} - ${c.categoryName}`);

    return [
      { field: 'recId', headerName: t('agendaActions.columns.recId'), width: 90, editable: false, cellDataType: 'number', sort: 'asc' },
      {
        field: 'logicalOrder',
        headerName: t('agendaActions.columns.logicalOrder'),
        width: 100,
        editable: true,
        cellDataType: 'number',
      },
      {
        field: 'scope',
        headerName: t('agendaActions.columns.scope'),
        width: 120,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: [...AGENDA_SCOPES] },
      },
      {
        field: 'categoryId',
        headerName: t('agendaActions.columns.categoryId'),
        width: 220,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: categoryValues },
        valueGetter: (p: ValueGetterParams<AgendaAction>) =>
          p.data ? (labelById.get(p.data.categoryId) ?? String(p.data.categoryId)) : '',
        valueSetter: (p: ValueSetterParams<AgendaAction>) => {
          if (!p.data) return false;
          const id = idByLabel.get(p.newValue);
          if (id === undefined) return false;
          p.data.categoryId = id;
          return true;
        },
      },
      { field: 'actionName', headerName: t('agendaActions.columns.actionName'), flex: 1, minWidth: 220, editable: true },
      { field: 'actionDescription', headerName: t('agendaActions.columns.actionDescription'), flex: 1, minWidth: 220, editable: true },
      boolCol('dueSunday', 90),
      boolCol('dueMonday', 90),
      boolCol('dueTuesday', 90),
      boolCol('dueWednesday', 90),
      boolCol('dueThursday', 90),
      boolCol('dueFriday', 90),
      boolCol('beforeOpenDay', 120),
      boolCol('required4ValuationIsraeli', 140),
      boolCol('required4ValuationForeign', 140),
      boolCol('ask4Comment', 120),
      boolCol('isActive', 100),
      { field: 'rowColor', headerName: t('agendaActions.columns.rowColor'), width: 100, editable: true, cellDataType: 'number' },
      { field: 'actionCode', headerName: t('agendaActions.columns.actionCode'), width: 140, editable: true },
    ];
  }, [categories]);

  if (error) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ color: 'var(--color-error-text)', background: 'var(--color-error-bg)', padding: '8px 12px', borderRadius: 4 }}>
          {t('agendaActions.errorPrefix', { message: error })}
        </div>
      </div>
    );
  }

  if (loading || !categories) {
    return <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>;
  }

  return (
    <LopCrudPage<AgendaAction>
      resourceCode="agendaActions"
      titleKey="agendaActions.pageTitle"
      idField="recId"
      getRows={getAgendaActions}
      saveBatch={(added, modified, deletedIds) =>
        saveAgendaActionsBatch(added, modified, deletedIds as number[])
      }
      columnDefs={columnDefs}
      newRowDefaults={newDefaults}
    />
  );
}
